/**
 * features/finance/AssetRegister.tsx
 * ------------------------------------------------------------------
 * WHAT THE SCHOOL OWNS, AND WHAT IT IS STILL WORTH.
 *
 * A minibus, a laptop trolley, the lab benches. Each was bought once and
 * used for years, so charging the whole cost to the month it was paid
 * for makes that month look terrible and every month after it better
 * than it was.
 *
 * BUT THE REGISTER IS A CONTROL BEFORE IT IS A CALCULATION. It is the
 * only record that says the minibus exists, where it is and who is
 * answerable for it. A school without one cannot answer "is it still
 * here?" — which is the question an auditor asks, and the question a
 * theft answers for you. So custodian and location sit on the row rather
 * than behind an edit screen, and an asset with neither is visible as a
 * gap rather than looking complete.
 *
 * DEPRECIATION IS COUNTED IN MONTHS. A minibus bought in November has
 * depreciated for two months by year end, not a year — and getting that
 * wrong persists for the whole life of the asset.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { collection, doc, onSnapshot, query, runTransaction } from 'firebase/firestore';
import {
  Boxes, Plus, X, AlertTriangle, TrendingDown, UserX, Archive,
} from 'lucide-react';
import { Spinner } from '@/components/ui/primitives';
import { useAuth } from '@/contexts/AuthContext';
import { db } from '@/lib/firebase';
import {
  ASSET_COLLECTION, ASSET_CLASSES, ASSET_STATUS_LABEL, assetCounterId, formatAssetNo,
  buildAssetRegister, assetProblem, disposalResult, annualCharge,
  type FixedAsset, type AssetPosition,
} from '@/lib/assets';
import {
  PageHeader, SoftCard, PanelHeader, StatChip, glassFieldClass, btnPrimary, btnGhost,
  modalBackdrop, modalSurface,
} from '@/features/finance/ui';

const COUNTERS = 'financeCounters';

const money = (n: number) =>
  n.toLocaleString('en-BW', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function AssetRegister() {
  const { userData } = useAuth();
  const thisYear = new Date().getFullYear();
  const [assets, setAssets] = useState<FixedAsset[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [year, setYear] = useState(String(thisYear));

  useEffect(() => onSnapshot(
    query(collection(db, ASSET_COLLECTION)),
    (s) => setAssets(s.docs.map((d) => ({ id: d.id, ...d.data() }) as FixedAsset)),
    () => setAssets([]),
  ), []);

  const report = useMemo(
    () => (assets ? buildAssetRegister(assets, `${year}-12-31`, `${year}-01-01`) : null),
    [assets, year],
  );

  const disposed = useMemo(
    () => (assets ?? []).filter((a) => a.status !== 'in_use'),
    [assets],
  );

  /* An asset nobody is answerable for. Not a calculation error — the
     register still adds up — but it is the reason the register exists,
     so it is a finding rather than a blank cell. */
  const unattended = useMemo(
    () => (report?.positions ?? []).filter((p) => !p.asset.custodian?.trim()),
    [report],
  );

  if (assets === null || !report) {
    return <div className="p-6 flex justify-center"><Spinner /></div>;
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <PageHeader
        icon={Boxes} tone="navy"
        title="Asset Register"
        subtitle="What the school owns, where it is, and what it is still worth"
        actions={(
          <div className="flex gap-2">
            <select value={year} onChange={(e) => setYear(e.target.value)}
              className={glassFieldClass('max-w-[110px] text-sm')}>
              {[0, 1, 2].map((n) => {
                const y = String(thisYear - n);
                return <option key={y} value={y}>{y}</option>;
              })}
            </select>
            <button type="button" className={btnPrimary} onClick={() => setAdding(true)}>
              <Plus size={15} /> Add an asset
            </button>
          </div>
        )}
      />

      {err && (
        <SoftCard className="mb-4 border-l-4 border-red-400">
          <p className="text-sm text-red-700 dark:text-red-300 flex items-start gap-2">
            <AlertTriangle size={15} className="mt-0.5 shrink-0" />{err}
          </p>
        </SoftCard>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-5">
        <StatChip tone="slate" value={money(report.cost)} label="Cost" />
        <StatChip tone="slate" value={money(report.accumulated)} label="Depreciated to date" />
        <StatChip tone="navy" icon={Boxes} value={money(report.carrying)} label="Carrying value" />
        <StatChip tone="amber" icon={TrendingDown}
          value={money(report.chargeForYear)} label={`Charge for ${year}`} />
      </div>

      {unattended.length > 0 && (
        <SoftCard className="mb-4 border-l-4 border-amber-400">
          <p className="text-sm text-amber-800 dark:text-amber-300">
            <UserX size={15} className="inline mr-1 -mt-0.5" />
            {unattended.length} asset{unattended.length === 1 ? ' has' : 's have'} nobody
            recorded as answerable for {unattended.length === 1 ? 'it' : 'them'}. The register
            still adds up — but "is it still here?" is the question it exists to answer.
          </p>
        </SoftCard>
      )}

      {report.byClass.length > 0 && (
        <SoftCard pad="p-0" className="mb-5">
          <PanelHeader icon={Boxes} tone="navy" title="By class" />
          <div className="p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {report.byClass.map((c) => (
              <div key={c.classKey} className="rounded-lg border border-gray-100 dark:border-gray-700/70 p-3">
                <p className="text-lg font-bold tabular-nums text-gray-900 dark:text-white">
                  {money(c.carrying)}
                </p>
                <p className="text-[11px] text-gray-400 mt-0.5">{c.label}</p>
                <p className="text-[11px] text-gray-400">{c.count} item{c.count === 1 ? '' : 's'}</p>
              </div>
            ))}
          </div>
        </SoftCard>
      )}

      <SoftCard pad="p-0">
        <PanelHeader icon={Boxes} tone="navy" title="Assets in use"
          subtitle="Straight line, counted in months from the month of purchase" />
        {report.positions.length === 0 ? (
          <p className="p-6 text-sm text-gray-500 text-center">
            Nothing on the register yet. Until something is here, the accounts cannot say what
            the school owns.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100 dark:border-gray-700/70">
                  <th className="py-2 pl-4 pr-2 font-semibold">Asset</th>
                  <th className="px-2 font-semibold">Where / who</th>
                  <th className="px-2 font-semibold text-right">Cost</th>
                  <th className="px-2 font-semibold text-right">Charge {year}</th>
                  <th className="px-2 font-semibold text-right">Depreciated</th>
                  <th className="px-2 pr-4 font-semibold text-right">Carrying</th>
                </tr>
              </thead>
              <tbody>
                {report.positions.map((p) => (
                  <Row key={p.asset.id} p={p} />
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-200 dark:border-gray-700 font-bold">
                  <td className="py-2.5 pl-4 pr-2" colSpan={2}>Total</td>
                  <td className="px-2 text-right tabular-nums">{money(report.cost)}</td>
                  <td className="px-2 text-right tabular-nums">{money(report.chargeForYear)}</td>
                  <td className="px-2 text-right tabular-nums">{money(report.accumulated)}</td>
                  <td className="px-2 pr-4 text-right tabular-nums">{money(report.carrying)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </SoftCard>

      {disposed.length > 0 && (
        <SoftCard pad="p-0" className="mt-5">
          <PanelHeader icon={Archive} tone="navy" title="Disposed of"
            subtitle="Kept, not deleted — the register has to say what happened to it" />
          <table className="w-full text-sm">
            <tbody>
              {disposed.map((a) => {
                const d = disposalResult(a, `${year}-12-31`);
                return (
                  <tr key={a.id} className="border-b border-gray-50 dark:border-gray-800">
                    <td className="py-2 pl-4 pr-2">
                      <span className="font-mono text-xs text-gray-500">{a.assetNo}</span>{' '}
                      <span className="text-gray-800 dark:text-gray-100">{a.description}</span>
                      <span className="block text-[11px] text-gray-400">
                        {ASSET_STATUS_LABEL[a.status]}
                        {a.disposedOn ? ` on ${a.disposedOn}` : ''}
                      </span>
                    </td>
                    <td className="px-2 text-right tabular-nums text-gray-500">
                      carried {money(d.carrying)}
                    </td>
                    <td className="px-2 text-right tabular-nums text-gray-500">
                      for {money(d.proceeds)}
                    </td>
                    <td className={`px-2 pr-4 text-right tabular-nums font-medium ${
                      d.isLoss ? 'text-red-700 dark:text-red-400' : 'text-emerald-700 dark:text-emerald-400'
                    }`}>
                      {d.isLoss ? 'loss ' : 'profit '}{money(Math.abs(d.result))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </SoftCard>
      )}

      {adding && (
        <AddAsset
          onClose={() => setAdding(false)}
          onError={setErr}
          actor={{ uid: userData?.uid ?? '' }}
        />
      )}
    </div>
  );
}

function Row({ p }: { p: AssetPosition }) {
  const a = p.asset;
  return (
    <tr className="border-b border-gray-50 dark:border-gray-800">
      <td className="py-2 pl-4 pr-2">
        <span className="font-mono text-xs text-gray-500">{a.assetNo}</span>{' '}
        <span className="text-gray-800 dark:text-gray-100">{a.description}</span>
        <span className="block text-[11px] text-gray-400">
          {ASSET_CLASSES.find((c) => c.key === a.classKey)?.label ?? a.classKey}
          {' · '}bought {a.acquiredOn}
          {' · '}{a.usefulLife}y
          {p.fullyDepreciated && ' · fully depreciated'}
        </span>
      </td>
      <td className="px-2 text-[13px]">
        {a.custodian?.trim()
          ? <span className="text-gray-700 dark:text-gray-200">{a.custodian}</span>
          : <span className="text-amber-600">nobody recorded</span>}
        {a.location && <span className="block text-[11px] text-gray-400">{a.location}</span>}
      </td>
      <td className="px-2 text-right tabular-nums">{money(a.cost)}</td>
      <td className="px-2 text-right tabular-nums text-gray-600 dark:text-gray-300">
        {p.chargeForYear ? money(p.chargeForYear) : '—'}
      </td>
      <td className="px-2 text-right tabular-nums text-gray-500">{money(p.accumulated)}</td>
      <td className="px-2 pr-4 text-right tabular-nums font-medium">{money(p.carrying)}</td>
    </tr>
  );
}

function AddAsset({
  onClose, onError, actor,
}: { onClose: () => void; onError: (m: string) => void; actor: { uid: string } }) {
  const [f, setF] = useState<Partial<FixedAsset>>({
    classKey: 'equipment',
    acquiredOn: new Date().toISOString().slice(0, 10),
    usefulLife: ASSET_CLASSES.find((c) => c.key === 'equipment')?.usefulLife ?? 8,
    residual: 0,
  });
  const [busy, setBusy] = useState(false);

  const problem = assetProblem(f);
  const preview = problem ? null : annualCharge(f as FixedAsset);

  async function save() {
    setBusy(true);
    try {
      const year = (f.acquiredOn ?? '').slice(0, 4);
      const at = new Date().toISOString();
      await runTransaction(db, async (tx) => {
        const counterRef = doc(db, COUNTERS, assetCounterId(year));
        const snap = await tx.get(counterRef);
        const seq = ((snap.exists() ? Number(snap.data().seq) : 0) || 0) + 1;
        tx.set(counterRef, { seq, updatedAt: at }, { merge: true });

        const ref = doc(collection(db, ASSET_COLLECTION));
        tx.set(ref, {
          ...f,
          id: ref.id,
          assetNo: formatAssetNo(seq, year),
          cost: Number(f.cost),
          residual: Number(f.residual) || 0,
          usefulLife: Number(f.usefulLife),
          status: 'in_use',
          createdAt: at,
          createdBy: actor.uid,
        } as FixedAsset);
      });
      onClose();
    } catch (e) {
      onError((e as Error).message);
    }
    setBusy(false);
  }

  const set = (patch: Partial<FixedAsset>) => setF({ ...f, ...patch });

  return (
    <div className={modalBackdrop} onClick={onClose}>
      <div className={modalSurface('max-w-2xl w-full')} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-700">
          <h3 className="font-bold text-gray-900 dark:text-white">Add an asset</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-3 max-h-[65vh] overflow-y-auto">
          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-gray-400">What is it</span>
            <input value={f.description ?? ''} onChange={(e) => set({ description: e.target.value })}
              className={glassFieldClass('mt-1')} placeholder="Toyota Hiace minibus" />
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-gray-400">Class</span>
              <select value={f.classKey}
                onChange={(e) => {
                  const c = ASSET_CLASSES.find((x) => x.key === e.target.value);
                  /* Changing the class resets the life to that class's
                     default. A minibus left on a computer's four years is
                     the error this prevents. */
                  set({ classKey: e.target.value, usefulLife: c?.usefulLife });
                }}
                className={glassFieldClass('mt-1')}>
                {ASSET_CLASSES.map((c) => (
                  <option key={c.key} value={c.key}>{c.label}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-gray-400">Bought on</span>
              <input type="date" value={f.acquiredOn ?? ''}
                onChange={(e) => set({ acquiredOn: e.target.value })}
                className={glassFieldClass('mt-1')} />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-gray-400">
                Useful life (years)
              </span>
              <input type="number" value={f.usefulLife ?? ''}
                onChange={(e) => set({ usefulLife: Number(e.target.value) })}
                className={glassFieldClass('mt-1 text-right')} />
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-gray-400">Cost</span>
              <input type="number" step="0.01" value={f.cost ?? ''}
                onChange={(e) => set({ cost: Number(e.target.value) })}
                className={glassFieldClass('mt-1 text-right')} />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-gray-400">
                Residual value
              </span>
              <input type="number" step="0.01" value={f.residual ?? 0}
                onChange={(e) => set({ residual: Number(e.target.value) })}
                className={glassFieldClass('mt-1 text-right')} />
              <span className="text-[11px] text-gray-400">
                What it will be worth at the end. Only cost LESS this is spread.
              </span>
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-gray-400">
                Who is answerable
              </span>
              <input value={f.custodian ?? ''} onChange={(e) => set({ custodian: e.target.value })}
                className={glassFieldClass('mt-1')} />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-gray-400">Where it is</span>
              <input value={f.location ?? ''} onChange={(e) => set({ location: e.target.value })}
                className={glassFieldClass('mt-1')} />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-gray-400">
                Serial / registration
              </span>
              <input value={f.serial ?? ''} onChange={(e) => set({ serial: e.target.value })}
                className={glassFieldClass('mt-1')} />
            </label>
          </div>

          {problem
            ? <p className="text-[13px] text-amber-700 dark:text-amber-300">{problem}</p>
            : (
              <p className="text-[13px] text-gray-600 dark:text-gray-300">
                Depreciates at <strong>{money(preview ?? 0)}</strong> a year
                ({money((preview ?? 0) / 12)} a month), from the month it was bought.
              </p>
            )}
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-gray-100 dark:border-gray-700">
          <button type="button" className={btnGhost} onClick={onClose}>Cancel</button>
          <button type="button" className={btnPrimary} onClick={save}
            disabled={busy || Boolean(problem)}>
            {busy ? 'Saving…' : 'Add it'}
          </button>
        </div>
      </div>
    </div>
  );
}
